import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

/**
 * Lazy-load the event registry for validation (dev warns, prod drops).
 */
let registryPromise = null;
function getRegistry() {
    if (!registryPromise) {
        registryPromise = import('../../../../analytics/events.json')
            .then(m => m.default || m)
            .catch(() => null);
    }
    return registryPromise;
}

/**
 * Initialize PostHog once per page.
 * Uses a window flag to guard across separate Vite bundles.
 */
export function initPostHog() {
    if (window.__wyndyPostHogInit || !POSTHOG_KEY) return;
    window.__wyndyPostHogInit = true;
    initialized = true;

    const isSignupPage = /^\/(sign-up|uabmedicine|wellthy|vivvi)/.test(window.location.pathname);

    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: !isSignupPage,
        disable_session_recording: true,
        capture_performance: { web_vitals: false },
        persistence: 'localStorage+cookie',
        cross_subdomain_cookie: true,
        cookie_domain: '.wyndy.com',
    });

    posthog.register({ platform: 'web' });

    // Pass ineligibility as a flag-only property (not $set — avoids creating person profiles).
    const isIneligible = document.cookie.split('; ').some(c => c === 'wyndy_exp_ineligible=1');
    posthog.setPersonPropertiesForFlags({ wyndy_exp_ineligible: isIneligible });
}

/**
 * Identify a parent after signup or returning sign-in.
 * Uses the same distinct ID format as the mobile app: 'parent-<id>'
 */
export function identifyParent(parentId) {
    if (!parentId || !initialized) return;
    posthog.identify(`parent-${parentId}`, { userType: 'parent' });
}

/**
 * Reset identity on logout so the next visitor isn't merged.
 */
export function resetIdentity() {
    if (!initialized) return;
    posthog.reset();
}

/**
 * Track a named event with properties.
 * Validates against the registry: warns in dev, silently drops in prod.
 */
export async function trackEvent(eventName, properties = {}) {
    if (!initialized) return;

    const data = await getRegistry();
    if (data?.events) {
        const def = data.events[eventName];
        if (!def) {
            if (import.meta.env.DEV) {
                console.warn(`[PostHog] Unregistered event: "${eventName}"`);
            }
            return;
        }
        if (import.meta.env.DEV && def.properties) {
            for (const [prop, propDef] of Object.entries(def.properties)) {
                if (propDef.required && !(prop in properties)) {
                    console.warn(`[PostHog] Missing required property "${prop}" for event "${eventName}"`);
                }
            }
        }
    }

    posthog.capture(eventName, properties);
}

/**
 * Navigate without capturing. Legacy CTA names are unregistered;
 * once #23 replaces them with CTA Clicked / CTA Viewed, this can capture again.
 */
export function trackLinkEvent(link) {
    if (link?.href) {
        window.location.href = link.href;
    }
    return false;
}

export { posthog };